import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ResolvedUvInserts } from '@shared/view/uv-inserts/resolve-uv-inserts';
import { type UVInsertConfig, isUvInsertUserConfigurableText } from '@shared/models/uv-inserts';
import {
  getFaceElementsForRegionTab,
  getSuggestionTargetTextPropertyKeyForRegionTab,
  getImageUploadPropertyKeysForRegionTab,
  getUvInsertFaceTabItems
} from '@shared/view/uv-inserts/visualiser-tabs';
import { getLiquidProductVariantImageSrc } from '@shared/view/uv-inserts/liquid-variant-image';

/**
 * The selection payload stored in `selections.slots[slotId].selected` for a UV_INSERT slot.
 * Pre-computes variantId and templateId so downstream cart code doesn't need resolvedUvInserts.
 */
export type UvInsertSlotSelection = {
  variantId: number;
  templateId: string | null;
  configHandle: string;
  /** Display title of the chosen theme variant — used in mini cart and summary. */
  themeTitle: string;
  textByKey: Record<string, string>;
  imageByKey: Record<string, File | null>;
};

type UsePersonalisationUvInsertSlotViewModelOptions = {
  /** Resolved UV insert data from PersonalisationContext. */
  resolvedUvInserts: ResolvedUvInserts | null;
  /** The config handle stored on the slot metaobject — used to find the correct UVInsertConfig. */
  configHandle: string | undefined;
  /**
   * Whether this slot is required (boolean or the string 'true' from Liquid/metaobject).
   * Required slots always opt in and never show the toggle.
   */
  isRequired: boolean | string | undefined;
  /**
   * Whether the toggle defaults to ON when the slot is optional (boolean or string 'true').
   * When true the insert is opted-in by default and the user must actively opt out.
   */
  uvInsertVisibleByDefault: boolean | string | undefined;
  /**
   * Whether the slot is free — if true, overrides the variant price to £0 with a strikethrough.
   * Matches the isFreeSlot pattern used by other slot types.
   */
  isFreeSlot: boolean | string | undefined;
  /**
   * Admin-configured fee override in pounds. When set, replaces the UV insert variant's
   * own product price for both the running total and the UI display.
   */
  overridePrice: number | null | undefined;
  /**
   * The existing selection from PersonalisationContext for this slot.
   * Populated when the user navigates back through the journey so the VM can restore its state.
   * Null means opted-out or no previous selection.
   */
  existingSelection: UvInsertSlotSelection | null | undefined;
  /** Called with the selection when opted in, or null when opted out. */
  onSelectionChange: (selection: UvInsertSlotSelection | null) => void;
};

/**
 * ViewModel for PersonalisationUvInsertSlot.
 *
 * Manages all UV insert UI state (tab, theme, text inputs, image uploads) for a single
 * personalisation slot, and notifies the context via `onSelectionChange` on every meaningful change.
 *
 * Key opt-in logic:
 * - Required slots (`isRequired = true`): `optedIn` defaults to true and the toggle is hidden.
 *   The insert is always added to `_personalisation_choices`, even if blank.
 * - Optional, visible by default (`uvInsertVisibleByDefault = true`): toggle is shown and starts
 *   checked — the user must actively opt out.
 * - Optional, hidden by default (`uvInsertVisibleByDefault = false`): toggle is shown and starts
 *   unchecked — the user must actively opt in.
 */
export function usePersonalisationUvInsertSlotViewModel({
  resolvedUvInserts,
  configHandle,
  isRequired,
  uvInsertVisibleByDefault,
  isFreeSlot,
  overridePrice,
  existingSelection,
  onSelectionChange
}: UsePersonalisationUvInsertSlotViewModelOptions) {
  // Normalise the isRequired field which can arrive as boolean or the string 'true'.
  const isSlotRequired = isRequired === true || isRequired === 'true';

  // Normalise uvInsertVisibleByDefault — controls the toggle's default checked state.
  const isVisibleByDefault = uvInsertVisibleByDefault === true || uvInsertVisibleByDefault === 'true';

  // Restore optedIn from an existing context selection when navigating back.
  // A non-null existing selection means the user previously toggled on.
  // Falls back to isRequired / isVisibleByDefault when there is no prior selection.
  const [optedIn, setOptedIn] = useState(() => {
    if (existingSelection != null) return true;
    return isSlotRequired || isVisibleByDefault;
  });

  // Restore themeIndex from the previously selected variant ID when navigating back.
  const [themeIndex, setThemeIndex] = useState(() => {
    if (!existingSelection?.variantId || !resolvedUvInserts) return 0;
    const config = resolvedUvInserts.configs.find((c) => c.handle === configHandle);
    if (!config) return 0;
    const idx = config.themes.findIndex((t) => Number(t.id) === existingSelection.variantId);
    return idx >= 0 ? idx : 0;
  });

  const [activeTabIndex, setActiveTabIndex] = useState(0);

  // Restore text inputs from the previous selection.
  const [textByKey, setTextByKey] = useState<Record<string, string>>(() => existingSelection?.textByKey ?? {});

  // Restore image files from the previous selection.
  // File objects remain in memory as long as the context is mounted, so back-navigation
  // restores them correctly within the same page session.
  const [imageByKey, setImageByKey] = useState<Record<string, File | null>>(() => existingSelection?.imageByKey ?? {});
  const [imageTransformByKey, setImageTransformByKey] = useState<
    Record<string, { scale: number; x: number; y: number }>
  >({});
  const [themeChooserOpen, setThemeChooserOpen] = useState(false);
  const [suggestionChooserOpen, setSuggestionChooserOpen] = useState(false);
  const [selectedSuggestionGroupIndex, setSelectedSuggestionGroupIndex] = useState(0);

  // Hold onSelectionChange in a ref so effects don't need to re-subscribe when the callback identity changes.
  const onSelectionChangeRef = useRef(onSelectionChange);
  useEffect(() => {
    onSelectionChangeRef.current = onSelectionChange;
  }, [onSelectionChange]);

  // Find the config matching this slot's configured handle.
  const activeConfig: UVInsertConfig | undefined = useMemo(
    () => resolvedUvInserts?.configs.find((c) => c.handle === configHandle),
    [resolvedUvInserts, configHandle]
  );

  // Reset to first tab whenever the active config changes.
  useEffect(() => {
    setActiveTabIndex(0);
  }, [activeConfig?.id]);

  // Reset suggestion group index when the tab changes.
  useEffect(() => {
    setSelectedSuggestionGroupIndex(0);
  }, [activeTabIndex]);

  // Seed textByKey with empty strings for each TEXT element when the config first loads.
  useEffect(() => {
    if (!activeConfig) return;
    setTextByKey((prev) => {
      const next = { ...prev };
      for (const face of activeConfig.faceElements) {
        for (const el of face.elementConfig) {
          if (
            isUvInsertUserConfigurableText(el) &&
            !(el.lineItemPropertyKey in next)
          ) {
            const initial =
              el.type === 'TEXT' && el.editable === false ? (el.value ?? '') : '';
            next[el.lineItemPropertyKey] = initial;
          }
        }
      }
      return next;
    });
  }, [activeConfig?.id]);

  // Notify the context whenever selection state changes.
  // When opted out, clears the selection (null) so the insert is excluded from choices.
  // When opted in, sends the full selection so the insert is included (even if blank).
  useEffect(() => {
    if (!activeConfig || !configHandle) return;

    if (!optedIn) {
      onSelectionChangeRef.current(null);
      return;
    }

    const variantId = Number(activeConfig.themes[themeIndex]?.id ?? 0);
    if (!variantId) return;
    const templateId = resolvedUvInserts?.templateIds[variantId] ?? null;
    const themeTitle = String(activeConfig.themes[themeIndex]?.title ?? '');
    onSelectionChangeRef.current({ variantId, templateId, configHandle, themeTitle, textByKey, imageByKey });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [themeIndex, textByKey, imageByKey, activeConfig, configHandle, resolvedUvInserts, optedIn]);

  const faceTabItems = useMemo(
    () => (activeConfig ? getUvInsertFaceTabItems({ faceElements: activeConfig.faceElements }) : []),
    [activeConfig]
  );

  const facesToShow = useMemo(
    () =>
      activeConfig
        ? getFaceElementsForRegionTab({ faceElements: activeConfig.faceElements, tabIndex: activeTabIndex })
        : [],
    [activeConfig, activeTabIndex]
  );

  const imageUploadKeysForActiveTab = useMemo(() => {
    if (!activeConfig) return [] as string[];
    return getImageUploadPropertyKeysForRegionTab({
      faceElements: activeConfig.faceElements,
      tabIndex: activeTabIndex
    });
  }, [activeConfig, activeTabIndex]);

  const firstTextKeyOnActiveTab = useMemo(() => {
    if (!activeConfig) return null;
    return getSuggestionTargetTextPropertyKeyForRegionTab({
      faceElements: activeConfig.faceElements,
      tabIndex: activeTabIndex,
    });
  }, [activeConfig, activeTabIndex]);

  const theme = activeConfig?.themes[themeIndex];
  const selectedThemeImageSrc = theme ? getLiquidProductVariantImageSrc({ variant: theme }) : null;

  // Price of the currently selected theme variant (in pounds), converted from the Liquid pence value.
  const variantPricePounds = useMemo(() => {
    if (!activeConfig) return null;
    const raw = activeConfig.themes[themeIndex]?.price;
    if (raw == null) return null;
    const pence = parseFloat(String(raw));
    return Number.isNaN(pence) ? null : pence / 100;
  }, [activeConfig, themeIndex]);

  /**
   * The price to display in the slot UI and add to the running total.
   * Priority: isFreeSlot (→ 0) > overridePrice > variant price from Liquid config.
   */
  const effectivePrice = useMemo((): number | null => {
    if (isFreeSlot === true || isFreeSlot === 'true') return 0;
    if (typeof overridePrice === 'number') return overridePrice;
    return variantPricePounds;
  }, [isFreeSlot, overridePrice, variantPricePounds]);

  /**
   * The original variant price — only populated when isFreeSlot is true, for strikethrough display.
   */
  const originalPrice = useMemo((): number | null => {
    if (isFreeSlot !== true && isFreeSlot !== 'true') return null;
    return variantPricePounds;
  }, [isFreeSlot, variantPricePounds]);

  const setText = useCallback((key: string, value: string) => {
    setTextByKey((prev) => ({ ...prev, [key]: value }));
  }, []);

  const onSuggestionPick = useCallback(
    (text: string) => {
      if (!firstTextKeyOnActiveTab) return;
      setTextByKey((prev) => ({ ...prev, [firstTextKeyOnActiveTab]: text }));
    },
    [firstTextKeyOnActiveTab]
  );

  return {
    isSlotRequired,
    optedIn,
    setOptedIn,
    activeConfig,
    effectivePrice,
    originalPrice,
    themeIndex,
    setThemeIndex,
    activeTabIndex,
    setActiveTabIndex,
    theme,
    selectedThemeImageSrc,
    faceTabItems,
    facesToShow,
    firstTextKeyOnActiveTab,
    textByKey,
    setText,
    imageByKey,
    setImageByKey,
    imageTransformByKey,
    setImageTransformByKey,
    imageUploadKeysForActiveTab,
    themeChooserOpen,
    setThemeChooserOpen,
    suggestionChooserOpen,
    setSuggestionChooserOpen,
    selectedSuggestionGroupIndex,
    setSelectedSuggestionGroupIndex,
    onSuggestionPick
  };
}
